import {FC, useMemo, useState} from 'react'
import {Form, InputGroup, Table} from 'react-bootstrap'

export type AgreementRow = {
  code: string
  label: string
  sublabel: string
  isEUN?: boolean
  iso2ForFlag?: string // when label is an economy, drive flag icon
}

type Props = {
  title: string
  columnLabels: [string, string]
  rows: AgreementRow[]
  selected: string[]
  onChange: (codes: string[]) => void
  showEunNote?: boolean
}

const AgreementSelectTable: FC<Props> = ({
  title,
  columnLabels,
  rows,
  selected,
  onChange,
  showEunNote,
}) => {
  const [query, setQuery] = useState('')

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [r.label, r.sublabel, r.code].some((f) => f?.toLowerCase().includes(q))
    )
  }, [rows, query])

  const allSelected =
    filteredRows.length > 0 && filteredRows.every((r) => selected.includes(r.code))

  const toggleAll = () => {
    const codes = filteredRows.map((r) => r.code)
    if (allSelected) {
      onChange(selected.filter((c) => !codes.includes(c)))
    } else {
      onChange(Array.from(new Set([...selected, ...codes])))
    }
  }
  const toggleOne = (code: string) =>
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code])

  if (rows.length === 0) return null

  return (
    <div className='mb-6'>
      <h5 className='text-gray-700 mb-3'>{title}</h5>
      <InputGroup size='sm' className='mb-3'>
        <InputGroup.Text>
          <span className='fa fa-search'></span>
        </InputGroup.Text>
        <Form.Control
          placeholder='Search...'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </InputGroup>
      <Table hover bordered size='sm' className='gy-2'>
        <thead className='table-dark'>
          <tr>
            <th style={{width: 40}}>
              <Form.Check type='checkbox' checked={allSelected} onChange={toggleAll} />
            </th>
            <th>{columnLabels[0]}</th>
            <th>{columnLabels[1]}</th>
          </tr>
        </thead>
        <tbody>
          {filteredRows.length === 0 && (
            <tr>
              <td colSpan={3} className='text-center text-muted'>
                No matches
              </td>
            </tr>
          )}
          {filteredRows.map((r) => (
            <tr key={r.code} className='align-middle'>
              <td>
                <Form.Check
                  type='checkbox'
                  checked={selected.includes(r.code)}
                  onChange={() => toggleOne(r.code)}
                />
              </td>
              <td>
                {r.iso2ForFlag && (
                  <span className={`fi fi-${r.iso2ForFlag.toLowerCase()} me-2`}></span>
                )}
                {r.label}
                {r.isEUN && <span className='text-danger'> *</span>}
              </td>
              <td>{r.sublabel}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      {showEunNote && (
        <small className='text-muted'>* European Union (EUN) is treated as a single bloc.</small>
      )}
    </div>
  )
}

export default AgreementSelectTable
